import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react'
import { SITE } from '@/data/site'
import { FOOTER_COLUMNS } from '@/data/navigation'

export default function Footer() {
  const year = new Date().getFullYear()

  return (
    <footer className="bg-secondary-900 text-secondary-200">
      {/* Main */}
      <div className="container-content py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <p className="font-heading text-white font-bold text-xl leading-tight">Coed y Celyn Hall</p>
              <p className="text-accent-400 text-sm font-medium mt-0.5">Betws-y-Coed · Snowdonia</p>
            </div>
            <p className="text-sm text-secondary-300 leading-relaxed mb-6">
              Six beautifully appointed self-catering apartments in a Grade II listed hall on the banks of the River Conwy. 4 Star Visit Wales. Open all year.
            </p>

            {/* Contact */}
            <div className="flex flex-col gap-3">
              <a href={SITE.phoneTel} className="flex items-center gap-2.5 text-sm hover:text-accent-400 transition-colors group">
                <Phone size={15} className="text-accent-500 shrink-0" />
                <span>{SITE.phone}</span>
              </a>
              {SITE.email && (
                <a href={`mailto:${SITE.email}`} className="flex items-center gap-2.5 text-sm hover:text-accent-400 transition-colors">
                  <Mail size={15} className="text-accent-500 shrink-0" />
                  <span>{SITE.email}</span>
                </a>
              )}
              <div className="flex items-start gap-2.5 text-sm">
                <MapPin size={15} className="text-accent-500 shrink-0 mt-0.5" />
                <span className="leading-snug">{SITE.address.full}</span>
              </div>
            </div>

            {/* Social */}
            <div className="flex items-center gap-3 mt-6">
              {SITE.social.facebook && (
                <a
                  href={SITE.social.facebook}
                  target="_blank" rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-accent-600 transition-colors text-xs font-bold"
                  aria-label="Facebook"
                >
                  f
                </a>
              )}
              {SITE.social.tripadvisor && (
                <a
                  href={SITE.social.tripadvisor}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-secondary-300 hover:text-accent-400 transition-colors"
                  aria-label="TripAdvisor"
                >
                  <ExternalLink size={12} />
                  <span>TripAdvisor</span>
                </a>
              )}
            </div>
          </div>

          {/* Nav columns */}
          {FOOTER_COLUMNS.map((col) => (
            <div key={col.heading}>
              <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">{col.heading}</h3>
              <ul className="flex flex-col gap-2.5">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-secondary-300 hover:text-accent-400 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Awards bar */}
      <div className="border-t border-white/10">
        <div className="container-content py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <span className="text-xs text-secondary-400">Accreditations:</span>
            {SITE.awards.map((award) => (
              <span key={award.title} className="text-xs text-secondary-300 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                {award.title}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/5">
        <div className="container-content py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-secondary-500">
          <p>© {year} {SITE.name}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms"  className="hover:text-secondary-300 transition-colors">Terms & Conditions</Link>
            <Link to="/access" className="hover:text-secondary-300 transition-colors">Access Statement</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
